<script setup lang="ts">
    import { __ } from '@/js/stores/expressions';
    import TextareaInput from '../../TextareaInput.vue';
    import CoachingFormTotal from './CoachingFormTotal.vue';
    import CoachingFormQuestions from './CoachingFormQuestions.vue';
    import CoachingFormSectionHeader from './CoachingFormSectionHeader.vue';
    import { ProtocolFormPointMap, ProtocolFormState } from './coachingForm.types';
    import ProtocolComments from '../protocol/detail/ProtocolComments.vue';
    import EchoButton from '../../EchoButton.vue';
    import { computed, watch } from 'vue';
    import SelectInput from '../../SelectInput.vue';
    import FormGroup from '../../FormGroup.vue';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { PermissionArea, QualityAssuranceModulePermissionFlag } from '@/js/consts/permissions';
    import { CheckIcon } from '@lucide/vue';

    const { hasPermission } = usePermissions();
    const canViewRestrictedComment = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.RestrictedComment
    );

    const protocolFormState = defineModel<ProtocolFormState>('protocolFormState', {
        required: true,
    });

    const props = defineProps<{
        preview: boolean;
        pointMap: ProtocolFormPointMap;
    }>();

    const shouldShowReasonCodes = computed(() => {
        if (!protocolFormState.value.reasonCodes) {
            return false;
        }

        const threshold = Number(protocolFormState.value.reasonCodes.threshold);
        const totalPoints = props.pointMap.total?.points ?? 0;

        return totalPoints <= threshold;
    });

    const childOptions = computed(() => {
        const reasonCodes = protocolFormState.value.reasonCodes;
        if (!reasonCodes || !reasonCodes.selectedParentId) return [];

        const selectedParent = reasonCodes.codes.find((p) => p.id === reasonCodes.selectedParentId);
        return selectedParent?.childCodes ?? [];
    });

    watch(
        () => protocolFormState.value.reasonCodes?.selectedParentId,
        (newVal, oldVal) => {
            if (oldVal && newVal !== oldVal && protocolFormState.value.reasonCodes) {
                protocolFormState.value.reasonCodes.selectedChildId = '';
            }
        }
    );
</script>
<template>
    <div class="w-full page-content-width flex flex-col gap-8">
        <template v-if="protocolFormState">
            <div class="flex flex-col gap-4">
                <details
                    v-for="(section, index) in protocolFormState.sections"
                    :key="section.id"
                    class="group rounded-lg border border-gray-light overflow-hidden"
                    open
                >
                    <summary
                        class="list-none block [&::-webkit-details-marker]:hidden cursor-pointer"
                    >
                        <CoachingFormSectionHeader
                            :section="section"
                            :points="pointMap[section.id]"
                            :section-index="Number(index)"
                        />
                    </summary>
                    <CoachingFormQuestions
                        v-if="section.questions"
                        :protocol-id="protocolFormState.protocolId"
                        :questions="section.questions"
                        :preview="preview"
                        :section-index="Number(index)"
                    />
                    <div v-if="section.useComments" class="p-5 border-t border-gray-light">
                        <div class="mb-3">
                            <h2 class="text-body-bold mb-1">
                                {{ __('comment') }}
                            </h2>
                            <TextareaInput
                                v-if="!preview"
                                v-model="section.comment"
                                :aria-label="section.title + __('comment')"
                                :rows="3"
                                :placeholder="__('coaching-form-add-comment-placeholder')"
                                class="w-1/2"
                            />
                            <p v-else class="text-body">
                                {{ section.comment }}
                            </p>
                        </div>
                        <ProtocolComments
                            :key="protocolFormState.protocolId"
                            :protocol-id="protocolFormState.protocolId"
                            :protocol-section-id="section.protocolSectionId"
                            :disabled="!preview"
                        />
                    </div>
                </details>
            </div>
        </template>
        <div
            v-if="protocolFormState.action"
            class="rounded-md bg-gray-extralight p-5 flex flex-col"
        >
            <h2 class="heading-md">
                {{ __('coaching-form-action-header') }}
            </h2>

            <p v-if="protocolFormState.action.description" class="text-body-bold mb-4">
                {{ protocolFormState.action.description }}
            </p>
            <TextareaInput
                v-if="!preview"
                v-model="protocolFormState.action.text"
                :aria-label="__('coaching-form-action-header')"
                :disabled="protocolFormState.action.completed"
                :class="[
                    'w-full min-h-16',
                    protocolFormState.action.completed ? 'opacity-70 cursor-not-allowed' : '',
                ]"
            />
            <div v-else class="rounded-md bg-white p-5 min-h-16 text-body whitespace-pre-wrap">
                {{ protocolFormState.action.text }}
            </div>
            <div class="flex mt-6">
                <EchoButton
                    :variant="protocolFormState.action.completed ? 'secondary' : 'primary'"
                    :disabled="preview"
                    @click="
                        !preview &&
                        (protocolFormState.action.completed = !protocolFormState.action.completed)
                    "
                >
                    {{
                        protocolFormState.action.completed
                            ? __('coaching-form-action-completed')
                            : __('coaching-form-action-mark-completed')
                    }}
                    <template v-if="protocolFormState.action.completed" #icon-right>
                        <CheckIcon />
                    </template>
                </EchoButton>
            </div>
        </div>
        <div
            v-if="shouldShowReasonCodes && protocolFormState.reasonCodes"
            class="rounded-md bg-gray-extralight p-5 flex flex-col"
        >
            <label class="heading-md" for="reason-code-parent-select">
                {{ protocolFormState.reasonCodes.title }}
            </label>
            <p v-if="protocolFormState.reasonCodes.description" class="text-body-bold mb-4">
                {{ protocolFormState.reasonCodes.description }}
            </p>
            <p v-if="protocolFormState.reasonCodes.reasonCodeDescription" class="text-body">
                {{ protocolFormState.reasonCodes.reasonCodeDescription }}
            </p>
            <div class="flex gap-4">
                <SelectInput
                    id="reason-code-parent-select"
                    v-model="protocolFormState.reasonCodes.selectedParentId"
                    size="large"
                    :disabled="preview"
                >
                    <option value="">{{ __('select-reason-code') }}</option>
                    <option
                        v-for="parent in protocolFormState.reasonCodes.codes"
                        :key="parent.id"
                        :value="parent.id"
                    >
                        {{ parent.code }}
                    </option>
                </SelectInput>

                <SelectInput
                    v-if="childOptions.length"
                    id="reason-code-child-select"
                    v-model="protocolFormState.reasonCodes.selectedChildId"
                    size="large"
                    :disabled="preview"
                    :aria-label="__('select-sub-reason-code')"
                >
                    <option value="">{{ __('select-sub-reason-code') }}</option>
                    <option v-for="child in childOptions" :key="child.id" :value="child.id">
                        {{ child.code }}
                    </option>
                </SelectInput>
            </div>
        </div>
        <div class="flex flex-col gap-8">
            <div
                v-if="protocolFormState.restrictedComment && canViewRestrictedComment"
                class="rounded-md bg-gray-extralight p-5"
            >
                <FormGroup>
                    <template #header>
                        <component
                            :is="preview ? 'span' : 'label'"
                            :for="preview ? undefined : 'coaching-form-restricted-comment'"
                        >
                            {{
                                protocolFormState.restrictedComment.title ||
                                __('coaching-form-restricted-comment')
                            }}
                        </component>
                    </template>

                    <template v-if="protocolFormState.restrictedComment.description" #help>
                        {{ protocolFormState.restrictedComment.description }}
                    </template>

                    <TextareaInput
                        v-if="!preview"
                        id="coaching-form-restricted-comment"
                        v-model="protocolFormState.restrictedComment.comment"
                        :disabled="preview"
                        :placeholder="__('coaching-form-add-comment-placeholder')"
                        class="w-full"
                    />
                    <p v-else class="text-body">
                        {{ protocolFormState.restrictedComment.comment || __('no-comment') }}
                    </p>
                </FormGroup>
            </div>

            <div class="flex flex-wrap items-start gap-6.5">
                <div v-if="protocolFormState.openComment" class="flex-1 min-w-70">
                    <FormGroup>
                        <template #header>
                            <component
                                :is="preview ? 'span' : 'label'"
                                :for="preview ? undefined : 'coaching-form-add-comment'"
                                class="heading-md normal-case"
                            >
                                {{
                                    protocolFormState.openComment.title ||
                                    __('coaching-form-free-comment')
                                }}
                            </component>
                        </template>

                        <template v-if="protocolFormState.openComment.description" #help>
                            {{ protocolFormState.openComment.description }}
                        </template>
                        <TextareaInput
                            v-if="!preview"
                            id="coaching-form-add-comment"
                            v-model="protocolFormState.openComment.comment"
                            :placeholder="__('coaching-form-add-comment-placeholder')"
                            :auto-resize="false"
                            class="w-1/2 h-20 open-comment-editor"
                        />
                        <p v-else class="text-body grow">
                            {{ protocolFormState.openComment.comment || __('no-comment') }}
                        </p>
                    </FormGroup>
                </div>

                <div class="ml-auto">
                    <h3 class="heading-md mb-2.5">
                        {{ __('coaching-form-total-score') }}
                    </h3>
                    <CoachingFormTotal :points="pointMap.total" />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .open-comment-editor {
        resize: none;
    }
</style>
